import { createFileRoute } from '@tanstack/react-router'
import { LegalLayout } from '@/components/layout/legal-layout'
import { breadcrumbSchema, seo } from '@/shared/lib/seo'

export const Route = createFileRoute('/terms')({
  head: () => ({
    ...seo({
      title: 'Terms of Service | ControlStack.ai',
      description:
        'The terms that apply when you use controlstack.ai or book a Workflow Review, including payments, refunds and intellectual property.',
      path: '/terms',
      jsonLd: [breadcrumbSchema('Terms of Service', '/terms')],
    }),
  }),
  component: TermsOfService,
})

function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="January 12, 2025">
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing and using controlstack.ai ("the Service"), you agree to
          be bound by these Terms of Service. If you do not agree to these
          terms, please do not use our Service.
        </p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>
          controlstack.ai provides 1:1 consulting sessions, along with
          educational content and open-source software, related to AI systems
          development and software engineering. We reserve the right to modify,
          suspend, or discontinue any aspect of the Service at any time.
        </p>
      </section>

      <section>
        <h2>3. User Accounts</h2>
        <p>When you create an account, you agree to:</p>
        <ul>
          <li>Provide accurate and complete information</li>
          <li>Maintain the security of your account credentials</li>
          <li>Notify us immediately of any unauthorized access</li>
          <li>Accept responsibility for all activities under your account</li>
        </ul>
      </section>

      <section>
        <h2>4. Purchases and Payments</h2>
        <p>For paid content and services:</p>
        <ul>
          <li>All prices are in GBP unless otherwise stated</li>
          <li>Payment is due at the time of purchase</li>
          <li>We use secure third-party payment processors</li>
          <li>Refund policies are outlined separately for each product</li>
        </ul>
      </section>

      <section>
        <h2>5. Intellectual Property</h2>
        <p>
          All content on controlstack.ai, including but not limited to text,
          graphics, logos, code examples, videos, and session materials, is the
          property of controlstack.ai and protected by intellectual property
          laws.
        </p>
        <p>You may not:</p>
        <ul>
          <li>Copy, reproduce, or distribute our content without permission</li>
          <li>Modify or create derivative works from our content</li>
          <li>Use our content for commercial purposes without authorization</li>
          <li>Remove any copyright or proprietary notices</li>
        </ul>
      </section>

      <section>
        <h2>6. User Content</h2>
        <p>
          If you submit content to our platform (comments, code, etc.), you
          grant us a non-exclusive, worldwide, royalty-free license to use,
          reproduce, and distribute that content in connection with our Service.
        </p>
      </section>

      <section>
        <h2>7. Prohibited Conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Violate any applicable laws or regulations</li>
          <li>Share your account credentials with others</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>
            Use automated systems to access the Service without permission
          </li>
          <li>Harass, abuse, or harm other users</li>
          <li>Post spam, malware, or malicious content</li>
        </ul>
      </section>

      <section>
        <h2>8. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE
          UNINTERRUPTED, SECURE, OR ERROR-FREE. YOUR USE OF THE SERVICE IS AT
          YOUR OWN RISK.
        </p>
      </section>

      <section>
        <h2>9. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, CONTROLSTACK.AI SHALL NOT BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
        </p>
      </section>

      <section>
        <h2>10. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless controlstack.ai and its
          affiliates from any claims, damages, or expenses arising from your use
          of the Service or violation of these Terms.
        </p>
      </section>

      <section>
        <h2>11. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of the jurisdiction in which controlstack.ai operates, without
          regard to conflict of law principles.
        </p>
      </section>

      <section>
        <h2>12. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify
          users of significant changes by posting a notice on our website.
          Continued use of the Service after changes constitutes acceptance of
          the modified Terms.
        </p>
      </section>

      <section>
        <h2>13. Contact</h2>
        <p>For questions about these Terms of Service, please contact us at:</p>
        <p>
          <strong>Email:</strong> legal@controlstack.ai
        </p>
      </section>
    </LegalLayout>
  )
}
